/**
 * useDesktopNotificationPermission
 *
 * Manages browser Notification API permission state for the vendor dashboard.
 * Handles the permission prompt lifecycle including:
 *   - Safari dual-form requestPermission() compatibility
 *   - Firefox private-browsing silent denial (does NOT permanently dismiss)
 *   - External permission changes via navigator.permissions.query (Chrome/Firefox)
 *   - Two dismiss modes: permanent ("No, thanks") and session-only ("×")
 */

import { useState, useCallback, useEffect } from 'react';

const DISMISSED_KEY = 'lov_desktop_notif_dismissed';
const SESSION_DISMISSED_KEY = 'lov_desktop_notif_session_dismissed';

export interface DesktopNotificationPermissionState {
  isSupported: boolean;
  shouldShowPrompt: boolean;
  isGranted: boolean;
  requestPermission: () => Promise<void>;
  /** Permanent dismiss — stored in localStorage, survives page reload. */
  dismiss: () => void;
  /** Session dismiss — stored in sessionStorage, clears when tab is closed. */
  sessionDismiss: () => void;
}

export function useDesktopNotificationPermission(): DesktopNotificationPermissionState {
  const isSupported =
    typeof window !== 'undefined' && 'Notification' in window && window.Notification != null;

  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (!isSupported || typeof Notification === 'undefined' || Notification == null) {
      return 'default';
    }
    return Notification.permission;
  });

  const [isDismissed, setIsDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISSED_KEY) === 'true'
  );

  const [isSessionDismissed, setIsSessionDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && sessionStorage.getItem(SESSION_DISMISSED_KEY) === 'true'
  );

  // Subscribe to external permission changes (user grants/revokes in browser settings).
  // Chrome and Firefox support navigator.permissions.query for 'notifications'.
  // Safari does not — we fall back silently.
  useEffect(() => {
    if (!isSupported) return;
    navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then((status) => {
        setPermission(status.state as NotificationPermission);
        status.onchange = () => {
          const next = status.state as NotificationPermission;
          setPermission(next);
          // If the user grants permission externally, clear any prior dismissal
          // so the banner doesn't linger unnecessarily.
          if (next === 'granted') {
            localStorage.setItem(DISMISSED_KEY, 'true');
            setIsDismissed(true);
          }
        };
      })
      .catch(() => {
        // Safari: Permissions API unavailable for notifications — use mount snapshot.
        setPermission(Notification.permission);
      });
  }, [isSupported]);

  const shouldShowPrompt =
    isSupported && permission === 'default' && !isDismissed && !isSessionDismissed;

  const isGranted = isSupported && permission === 'granted';

  const requestPermission = useCallback(async () => {
    if (!isSupported) return;
    try {
      // Safari ≤15.3 uses a callback-based API. Normalize both forms to a Promise.
      const result: NotificationPermission = await new Promise((resolve) => {
        const ret = Notification.requestPermission((perm) => resolve(perm));
        // Modern browsers return a Promise; older Safari returns undefined.
        if (ret instanceof Promise) {
          ret.then(resolve).catch(() => resolve('denied'));
        }
      });

      setPermission(result);

      if (result === 'granted') {
        // Permission granted — persist dismissal so the banner never shows again.
        localStorage.setItem(DISMISSED_KEY, 'true');
        setIsDismissed(true);
      }
      // For 'denied': do NOT set DISMISSED_KEY. In Firefox private browsing the
      // browser silently returns 'denied' without showing a prompt — permanently
      // dismissing here would lock the vendor out when they switch to a normal window.
      // The banner will remain hidden because permission === 'denied' (not 'default').
    } catch {
      // requestPermission() threw (e.g. called outside a user gesture in some
      // browsers). Fail silently — don't dismiss the banner.
    }
  }, [isSupported]);

  /** Permanent dismiss — stored in localStorage. Written when user clicks "No, thanks". */
  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setIsDismissed(true);
  }, []);

  /** Session-only dismiss — stored in sessionStorage. Written when user clicks "×". */
  const sessionDismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
    setIsSessionDismissed(true);
  }, []);

  return { isSupported, shouldShowPrompt, isGranted, requestPermission, dismiss, sessionDismiss };
}
